/**
 * KpiBuilderChat — Cognara-powered drawer that lets a user chat their way to a
 * custom KPI widget for the SWM KPIs & Analytics dashboard.
 *
 * Interaction model:
 *   1. User types free text ("I want to see fabrication achievement by area").
 *   2. Cognara replies with either a clarifying question + quick-reply chips
 *      (status:"ask") or a finalised spec + computed preview (status:"ready").
 *   3. Preview renders inline with Recharts. User can Refine (keep chatting)
 *      or Save (persist to dashboard).
 */
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, X, Save, RefreshCw, Loader2, Wand2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { WidgetChart } from './WidgetChart';

const STARTER_PROMPTS = [
  'Fabrication achievement by area',
  'Erection progress by priority',
  'Marks stuck in QC hold',
  '6-month fabrication trend',
];

export function KpiBuilderChat({ open, onClose, projectId, onSaved }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm Cognara. Tell me what you want to track — a metric, a breakdown, a trend — and I'll build the chart for you.", options: STARTER_PROMPTS },
  ]);
  const [input, setInput]   = useState('');
  const [busy, setBusy]     = useState(false);
  const [preview, setPreview] = useState(null); // { spec, preview }
  const [saving, setSaving] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, preview]);

  const send = async (text) => {
    const clean = (text || '').trim();
    if (!clean || busy) return;
    const nextMessages = [...messages, { role: 'user', content: clean }];
    setMessages(nextMessages);
    setInput('');
    setBusy(true);
    setPreview(null);
    try {
      const res = await api.post(`/v1/projects/${projectId}/swm/kpi-builder/chat`,
        { messages: nextMessages.map(m => ({ role: m.role, content: m.content })) });
      if (res.data?.status === 'ready') {
        setPreview({ spec: res.data.spec, preview: res.data.preview });
        setMessages([...nextMessages, {
          role: 'assistant',
          content: `Here's a preview of "${res.data.spec.title}". Save to add it to your dashboard, or tell me what to change.`,
        }]);
      } else {
        setMessages([...nextMessages, {
          role: 'assistant',
          content: res.data?.question || 'Anything else?',
          options: res.data?.options || [],
        }]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cognara request failed');
      setMessages([...nextMessages, {
        role: 'assistant',
        content: '⚠ Cognara is unreachable right now. Please try again in a moment.',
      }]);
    } finally { setBusy(false); }
  };

  const savePreview = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const res = await api.post(`/v1/projects/${projectId}/swm/kpi-builder/widgets`, { spec: preview.spec });
      toast.success(`Added "${preview.spec.title}" to your dashboard`);
      onSaved?.(res.data);
      // Reset for the next widget
      setPreview(null);
      setMessages([
        { role: 'assistant', content: '✓ Added. Want to build another? Describe the next one.', options: STARTER_PROMPTS },
      ]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} data-testid="kpi-builder-backdrop" />
          <motion.div className="fixed right-0 top-0 bottom-0 z-50 w-[520px] max-w-full shadow-2xl flex flex-col"
            style={{ background: 'var(--card-bg)', color: 'var(--text-primary)' }}
            initial={{ x: 560 }} animate={{ x: 0 }} exit={{ x: 560 }}
            transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            data-testid="kpi-builder-drawer">
            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between shrink-0"
                 style={{ borderBottom: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                     style={{ background: 'linear-gradient(135deg,#F26B1F,#8b5cf6)' }}>
                  <Sparkles className="w-4 h-4 text-white" strokeWidth={2} />
                </div>
                <div>
                  <div className="text-sm font-bold">Build a KPI with Cognara</div>
                  <div className="text-[10px] opacity-60">Powered by Cognara Intelligence</div>
                </div>
              </div>
              <button onClick={onClose} data-testid="kpi-builder-close"
                className="w-8 h-8 rounded-md hover:bg-black/10 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Chat + preview scroll area */}
            <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="kpi-builder-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm`}
                       style={{
                         background: m.role === 'user' ? '#F26B1F' : 'var(--card-bg-alt, rgba(148,163,184,0.12))',
                         color:      m.role === 'user' ? '#fff'    : 'var(--text-primary)',
                       }}>
                    <div className="whitespace-pre-wrap">{m.content}</div>
                    {!!m.options?.length && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {m.options.map((opt, oi) => (
                          <button key={oi} onClick={() => send(opt)} disabled={busy}
                            data-testid={`kpi-builder-option-${oi}`}
                            className="text-[11px] px-2.5 py-1 rounded-full font-medium transition-all disabled:opacity-40"
                            style={{ background: 'var(--card-bg)', color: '#F26B1F',
                                     border: '1px solid rgba(242,107,31,0.35)' }}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {busy && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl px-3.5 py-2 text-sm flex items-center gap-2"
                       style={{ background: 'var(--card-bg-alt, rgba(148,163,184,0.12))' }}>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: '#F26B1F' }} />
                    <span className="opacity-70">Cognara is thinking…</span>
                  </div>
                </div>
              )}

              {preview && (
                <div className="rounded-xl p-3 border"
                     style={{ background: 'var(--card-bg)', borderColor: 'rgba(242,107,31,0.28)' }}
                     data-testid="kpi-builder-preview">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="text-[10px] tracking-widest uppercase font-bold" style={{ color: '#F26B1F' }}>Preview</div>
                      <div className="text-sm font-bold truncate">{preview.spec.title}</div>
                      <div className="text-[10px] opacity-60 truncate">
                        {preview.spec.chart_type} · {preview.spec.dataset} · {preview.spec.metric}
                        {preview.spec.group_by ? ` · by ${preview.spec.group_by}` : ''}
                      </div>
                    </div>
                    <button onClick={savePreview} disabled={saving} data-testid="kpi-builder-save"
                      className="shrink-0 px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-all disabled:opacity-50"
                      style={{ background: '#F26B1F', color: '#fff' }}>
                      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Save to dashboard
                    </button>
                  </div>
                  <div className="h-52">
                    <WidgetChart spec={preview.spec} data={preview.preview} compact />
                  </div>
                  {preview.preview?.series?.length === 0 && (
                    <div className="flex items-center gap-2 mt-2 text-xs opacity-70">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      No data yet — try a different filter or dataset.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={(e) => { e.preventDefault(); send(input); }}
                  className="p-3 shrink-0" style={{ borderTop: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)} disabled={busy}
                  placeholder="Describe the KPI you want…" data-testid="kpi-builder-input"
                  className="flex-1 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F26B1F]/40 transition-all"
                  style={{ background: 'var(--card-bg-alt, rgba(148,163,184,0.12))',
                           color: 'var(--text-primary)', border: '1px solid var(--border-color)' }} />
                <button type="submit" disabled={busy || !input.trim()} data-testid="kpi-builder-send"
                  className="w-9 h-9 rounded-lg flex items-center justify-center transition-all disabled:opacity-40"
                  style={{ background: '#F26B1F', color: '#fff' }}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-[10px] opacity-50 mt-1.5 flex items-center gap-1">
                <Wand2 className="w-2.5 h-2.5" />
                Cognara asks follow-ups until it can build a precise chart.
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
